import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Checkbox, TextField, IconButton,
  Typography, Button, Tooltip, TableSortLabel, Snackbar, Alert,
  Menu, MenuItem, Stack, InputAdornment, ListItemIcon, Select, FormControl,
  useMediaQuery, useTheme, Card, Divider, Grid, Chip
} from '@mui/material';
import {
  InboxOutlined,
  FileDownload as DownloadIcon, CalendarMonth as CalendarIcon,
  Search, Delete, Visibility, MoreVert, Share, Refresh,
} from '@mui/icons-material';
import ReactPaginate from 'react-paginate';
import './Pagination.css';
import Skeleton from '@mui/material/Skeleton';
import { dataAPI } from '../services/api';
import {
  AC, G, isProcessingStatus, getEntryDate, getAssetDownloadUrl, defaultVaultSearch, resolveRowTitle,
} from '../utils/mediaVault';
import {
  fetchVaultCached, readVaultCacheSync, invalidateVaultCache, VAULT_CACHE_KEYS,
} from '../utils/vaultCache';

const PAGE_SIZE_OPTIONS = [10, 25, 50];
const getUser = () => JSON.parse(localStorage.getItem('user') || '{}');

export default function ResultsTable({
  fetchFn,
  columns,
  viewPath,
  collectionName,
  searchFilter,
  searchPlaceholder = 'Search…',
  emptyTitle = 'No results yet',
  emptySubtitle = 'Your processed files will appear here.',
  emptyActionLabel = 'Open Studio',
  studioPath = null,
  sortKey,
  dateKey = 'date',
  refreshKey = 0,
  cacheKey = null,
  onEntriesLoaded,
}) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const fetchFnRef = useRef(fetchFn);
  const onLoadedRef = useRef(onEntriesLoaded);
  const hasEntriesRef = useRef(false);
  fetchFnRef.current = fetchFn;
  onLoadedRef.current = onEntriesLoaded;

  const resolveCacheKey = cacheKey || collectionName || 'table';

  const [entries, setEntries] = useState(() => {
    const { uid, userId } = getUser();
    const id = uid || userId;
    if (!id) return [];
    const cached = readVaultCacheSync(id, resolveCacheKey);
    return Array.isArray(cached?.entries) ? cached.entries : [];
  });
  const [initialLoading, setInitialLoading] = useState(() => entries.length === 0);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(PAGE_SIZE_OPTIONS[0]);
  const resolvedSortKey = sortKey ?? dateKey;
  const [orderBy, setOrderBy] = useState(resolvedSortKey);
  const [order, setOrder] = useState('desc');
  const [selected, setSelected] = useState([]);
  const [anchor, setAnchor] = useState(null);
  const [activeRow, setActiveRow] = useState(null);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'info' });
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showDateFilter, setShowDateFilter] = useState(false);

  useEffect(() => {
    hasEntriesRef.current = entries.length > 0;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const notify = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  const applyEntries = useCallback((list) => {
    hasEntriesRef.current = list.length > 0;
    setEntries(list);
    onLoadedRef.current?.(list);
  }, []);

  const fetchData = useCallback(async ({ force = false, silent = false } = {}) => {
    const { uid, userId } = getUser();
    const id = uid || userId;
    if (!id) {
      setInitialLoading(false);
      return;
    }

    if (!silent && !hasEntriesRef.current) setInitialLoading(true);
    else if (silent) setRefreshing(true);

    setLoadError(false);
    try {
      const { data: res } = await fetchVaultCached(
        id,
        resolveCacheKey,
        () => fetchFnRef.current(id),
        { force }
      );
      const list = Array.isArray(res?.entries) ? res.entries : [];
      applyEntries(list);
    } catch {
      if (!silent && !hasEntriesRef.current) setLoadError(true);
    } finally {
      setInitialLoading(false);
      setRefreshing(false);
    }
  }, [applyEntries, resolveCacheKey]);

  const refreshKeyRef = useRef(refreshKey);
  useEffect(() => {
    const force = refreshKey !== refreshKeyRef.current;
    refreshKeyRef.current = refreshKey;
    if (force) {
      const { uid, userId } = getUser();
      const id = uid || userId;
      if (id) invalidateVaultCache(id, resolveCacheKey);
    }
    fetchData({ force, silent: hasEntriesRef.current });
  }, [refreshKey, resolveCacheKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const hasProcessing = entries.some(r => isProcessingStatus(r.status));
    if (!hasProcessing) return undefined;
    const iv = setInterval(() => fetchData({ force: true, silent: true }), 15000);
    return () => clearInterval(iv);
  }, [entries, fetchData]);

  const handleSort = (field) => {
    setOrder(orderBy === field && order === 'asc' ? 'desc' : 'asc');
    setOrderBy(field);
  };

  const toggleSelectAll = (e) =>
    setSelected(e.target.checked ? entries.map(r => r.doc_id) : []);

  const toggleSelect = (id) =>
    setSelected(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );

  const exportCSV = () => {
    if (filtered.length === 0) return;
    const headers = columns.map(c => c.label).join(',');
    const rows = filtered.map(r =>
      columns.map(c => {
        const val = r[c.id] ?? '';
        return `"${String(val).replace(/"/g, '""')}"`;
      }).join(',')
    ).join('\n');
    const blob = new Blob([`${headers}\n${rows}`], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'export.csv'; a.click();
    URL.revokeObjectURL(url);
    notify('CSV exported');
  };

  const filtered = useMemo(() => {
    return entries.filter(r => {
      if (filter && !(searchFilter ? searchFilter(r, filter) : defaultVaultSearch(r, filter))) return false;
      if (dateFrom || dateTo) {
        const d = new Date(getEntryDate(r) || 0);
        if (dateFrom && d < new Date(dateFrom)) return false;
        if (dateTo && d > new Date(dateTo + 'T23:59:59')) return false;
      }
      return true;
    });
  }, [entries, filter, searchFilter, dateFrom, dateTo, dateKey]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      if (orderBy === resolvedSortKey || orderBy === dateKey || orderBy === 'date' || orderBy === 'Date') {
        const da = new Date(getEntryDate(a) || 0);
        const db = new Date(getEntryDate(b) || 0);
        return order === 'asc' ? da - db : db - da;
      }
      if (orderBy === 'title') {
        const va = resolveRowTitle(a);
        const vb = resolveRowTitle(b);
        return order === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      const va = (a[orderBy] ?? '').toString();
      const vb = (b[orderBy] ?? '').toString();
      return order === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    });
  }, [filtered, orderBy, order, dateKey, resolvedSortKey]);

  const displayed = useMemo(() => sorted.slice(page * perPage, (page + 1) * perPage), [sorted, page, perPage]);

  const handleView = useCallback((id, row) => {
    const path = viewPath?.(id);
    if (path) navigate(path);
    else {
      const url = getAssetDownloadUrl(row);
      if (url) window.open(url, '_blank');
    }
  }, [navigate, viewPath]);

  const copyShareLink = (row) => {
    const path = viewPath?.(row?.doc_id);
    const url = path
      ? `${window.location.origin}${path}`
      : `${window.location.origin}/dashboard/history`;
    navigator.clipboard.writeText(url).then(() => notify('Link copied to clipboard')).catch(() => notify('Could not copy link', 'error'));
  };

  const handleMenuAction = async (action) => {
    if (action === 'view') handleView(activeRow?.doc_id, activeRow);
    if (action === 'download' && activeRow) {
      const url = getAssetDownloadUrl(activeRow);
      if (url) window.open(url, '_blank');
      else notify('No download available for this item', 'warning');
    }
    if (action === 'delete' && activeRow) {
      if (window.confirm('Are you sure you want to permanently delete this record?')) {
        try {
          const coll = activeRow.collection || collectionName;
          if (!coll) { notify('Error: Collection name missing', 'error'); return; }
          await dataAPI.deleteRecord(coll, activeRow.doc_id);
          const { uid, userId } = getUser();
          const id = uid || userId;
          if (id) {
            invalidateVaultCache(id, resolveCacheKey);
            invalidateVaultCache(id, VAULT_CACHE_KEYS.ALL_ACTIVITY);
          }
          setEntries(prev => prev.filter(r => r.doc_id !== activeRow.doc_id));
          notify('Record deleted permanently');
        } catch (err) {
          console.error('[ResultsTable] Delete failed:', err);
          notify('Failed to delete record', 'error');
        }
      }
    }
    if (action === 'share' && activeRow) copyShareLink(activeRow);
    setAnchor(null);
    setActiveRow(null);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ width: '100%', p: { xs: 1.5, md: 2.5 }, borderRadius: '20px', background: 'rgba(17, 17, 17,0.03)', border: '1px solid rgba(17, 17, 17,0.07)' }}>
        {/* Toolbar */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 2.5 }}>
          <TextField
            fullWidth size="small"
            placeholder={searchPlaceholder}
            value={filter}
            onChange={e => { setFilter(e.target.value); setPage(0); }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment>,
              sx: { borderRadius: 1.5 },
            }}
          />
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
            {selected.length > 0 && (
              <Button size="small" variant="outlined" color="error" startIcon={<Delete />}
                sx={{ whiteSpace: 'nowrap' }}
                onClick={async () => {
                  if (window.confirm(`Are you sure you want to permanently delete ${selected.length} records?`)) {
                    try {
                      await Promise.all(selected.map(id => {
                        const row = entries.find(r => r.doc_id === id);
                        const coll = row?.collection || collectionName;
                        return dataAPI.deleteRecord(coll, id);
                      }));
                      setEntries(prev => prev.filter(r => !selected.includes(r.doc_id)));
                      setSelected([]);
                      notify(`${selected.length} items deleted permanently`);
                    } catch (err) {
                      notify('Failed to delete some items', 'error');
                    }
                  }
                }}>
                Del ({selected.length})
              </Button>
            )}
            <Tooltip title="Refresh">
              <IconButton
                size="small"
                onClick={() => {
                  const { uid, userId } = getUser();
                  const id = uid || userId;
                  if (id) invalidateVaultCache(id, resolveCacheKey);
                  fetchData({ force: true, silent: hasEntriesRef.current });
                }}
                sx={{ flexShrink: 0, opacity: refreshing ? 0.5 : 1 }}
              >
                <Refresh fontSize="small" sx={{ animation: refreshing ? 'spin 1s linear infinite' : 'none', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Filter by date">
              <IconButton size="small" onClick={() => setShowDateFilter(v => !v)}
                sx={{ flexShrink: 0, color: (dateFrom || dateTo) ? AC : 'inherit' }}>
                <CalendarIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {!isMobile && (
              <Tooltip title="Export CSV">
                <IconButton size="small" onClick={exportCSV} sx={{ flexShrink: 0 }}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <FormControl size="small" sx={{ minWidth: 90, flexShrink: 0 }}>
              <Select
                value={perPage}
                onChange={e => { setPerPage(Number(e.target.value)); setPage(0); }}
                sx={{ borderRadius: 1.5, fontSize: '0.85rem' }}
              >
                {PAGE_SIZE_OPTIONS.map(n => (
                  <MenuItem key={n} value={n}>{n}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Stack>

        {showDateFilter && (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <TextField size="small" type="date" label="From" value={dateFrom}
              onChange={e => { setDateFrom(e.target.value); setPage(0); }}
              InputLabelProps={{ shrink: true }}
              sx={{ borderRadius: 1.5, minWidth: 140, flex: 1 }}
            />
            <TextField size="small" type="date" label="To" value={dateTo}
              onChange={e => { setDateTo(e.target.value); setPage(0); }}
              InputLabelProps={{ shrink: true }}
              sx={{ borderRadius: 1.5, minWidth: 140, flex: 1 }}
            />
            {(dateFrom || dateTo) && (
              <Button size="small" variant="text" onClick={() => { setDateFrom(''); setDateTo(''); setPage(0); }}>
                Clear
              </Button>
            )}
          </Stack>
        )}

        {loadError && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }} action={<Button color="inherit" size="small" onClick={() => fetchData({ force: true })}>Retry</Button>}>
            Could not load records. Try refreshing.
          </Alert>
        )}

        {/* Content */}
        {initialLoading ? (
          <Box sx={{ px: 1, pt: 1 }}>
            {[...Array(5)].map((_, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, py: 0.5 }}>
                <Skeleton variant="rectangular" width={18} height={18} sx={{ borderRadius: '4px', bgcolor: 'rgba(17, 17, 17,0.07)', flexShrink: 0 }} />
                <Skeleton variant="text" sx={{ flex: 1, height: 20, bgcolor: 'rgba(17, 17, 17, 0.05)' }} />
                <Skeleton variant="circular" width={28} height={28} sx={{ bgcolor: 'rgba(17, 17, 17, 0.05)', flexShrink: 0 }} />
              </Box>
            ))}
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, px: 2, textAlign: 'center' }}>
            <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: 'rgba(17, 17, 17,0.02)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
              <InboxOutlined sx={{ fontSize: 32, color: 'text.disabled' }} />
            </Box>
            <Typography variant="subtitle1" fontWeight={600} mb={0.5}>{filter ? 'No results' : emptyTitle}</Typography>
            <Typography variant="body2" color="text.secondary" mb={2.5}>{filter ? `No results for "${filter}"` : emptySubtitle}</Typography>
            {!filter && studioPath && (
              <Button variant="contained" onClick={() => navigate(studioPath)} sx={{ background: G, fontWeight: 800, borderRadius: '12px', textTransform: 'none' }}>
                {emptyActionLabel}
              </Button>
            )}
          </Box>
        ) : (
        <Box sx={{ opacity: refreshing ? 0.92 : 1, transition: 'opacity 0.15s ease' }}>
        {isMobile ? (
          /* ---- Mobile Card View ---- */
          <Stack spacing={2}>
            {displayed.map(row => (
              <Card key={row.doc_id} sx={{ 
                p: 2, bgcolor: 'rgba(17, 17, 17,0.02)', border: '1px solid rgba(17, 17, 17, 0.05)', borderRadius: '12px',
                '&:hover': { background: 'rgba(17, 17, 17,0.04)' }
              }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                  <Checkbox size="small" checked={selected.includes(row.doc_id)} onChange={() => toggleSelect(row.doc_id)} sx={{ p: 0.5 }} />
                  <Stack direction="row" spacing={0.5}>
                    {getAssetDownloadUrl(row) && (
                      <IconButton size="small" component="a" href={getAssetDownloadUrl(row)} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
                        <DownloadIcon sx={{ fontSize: 18, color: AC }} />
                      </IconButton>
                    )}
                    <IconButton size="small" onClick={() => handleView(row.doc_id, row)}><Visibility sx={{ fontSize: 18, color: AC }} /></IconButton>
                    <IconButton size="small" onClick={e => { setAnchor(e.currentTarget); setActiveRow(row); }}><MoreVert sx={{ fontSize: 18 }} /></IconButton>
                  </Stack>
                </Stack>
                <Box onClick={() => handleView(row.doc_id, row)} sx={{ cursor: 'pointer' }}>
                  {columns.map(col => (
                    <Box key={col.id} sx={{ mb: 1.5, '&:last-child': { mb: 0 } }}>
                      <Typography variant="caption" sx={{ color: 'rgba(17, 17, 17,0.3)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 0.3 }}>
                        {col.label}
                      </Typography>
                      {col.render ? col.render(row) : <Typography variant="body2" sx={{ color: '#111111' }}>{row[col.id] || '—'}</Typography>}
                    </Box>
                  ))}
                </Box>
              </Card>
            ))}
          </Stack>
        ) : (
          /* ---- Desktop Card Grid View ---- */
          <>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: '0.78rem', color: 'rgba(17,17,17,0.45)', fontWeight: 700 }}>
                {filtered.length} assets
              </Typography>
              <Button
                size="small"
                variant="text"
                onClick={() => setSelected(selected.length === filtered.length ? [] : filtered.map(r => r.doc_id))}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                {selected.length === filtered.length ? 'Clear selection' : 'Select all'}
              </Button>
            </Stack>
            <Grid container spacing={2}>
              {displayed.map(row => (
                <Grid item xs={12} sm={6} lg={4} key={row.doc_id}>
                  <Card sx={{
                    p: 2, bgcolor: 'rgba(17, 17, 17,0.02)', border: '1px solid rgba(17, 17, 17, 0.05)', borderRadius: '14px',
                    transition: 'all .2s ease',
                    boxShadow: '0 1px 0 rgba(17,17,17,0.02)',
                    '&:hover': { background: 'rgba(17, 17, 17,0.035)', transform: 'translateY(-1px)', boxShadow: '0 10px 30px rgba(17,17,17,0.08)' }
                  }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.25}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Checkbox size="small" checked={selected.includes(row.doc_id)} onChange={() => toggleSelect(row.doc_id)} sx={{ p: 0.5 }} />
                        <Chip
                          size="small"
                          label={isProcessingStatus(row.status) ? 'Processing' : (row.status || 'Complete')}
                          sx={{
                            height: 22,
                            fontSize: '0.66rem',
                            fontWeight: 700,
                            bgcolor: isProcessingStatus(row.status) ? 'rgba(245,158,11,0.12)' : 'rgba(16,185,129,0.12)',
                            color: isProcessingStatus(row.status) ? '#b45309' : '#059669',
                            border: `1px solid ${isProcessingStatus(row.status) ? 'rgba(245,158,11,0.25)' : 'rgba(16,185,129,0.22)'}`,
                          }}
                        />
                      </Stack>
                      <Stack direction="row" spacing={0.5}>
                        {getAssetDownloadUrl(row) && (
                          <IconButton size="small" component="a" href={getAssetDownloadUrl(row)} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
                            <DownloadIcon sx={{ fontSize: 18, color: AC }} />
                          </IconButton>
                        )}
                        <IconButton size="small" onClick={() => handleView(row.doc_id, row)}>
                          <Visibility sx={{ fontSize: 18, color: AC }} />
                        </IconButton>
                        <IconButton size="small" onClick={e => { setAnchor(e.currentTarget); setActiveRow(row); }}>
                          <MoreVert sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Stack>
                    </Stack>
                    <Typography sx={{ fontSize: '0.72rem', color: 'rgba(17,17,17,0.45)', fontWeight: 600, mb: 1 }}>
                      {new Date(getEntryDate(row) || Date.now()).toLocaleString()}
                    </Typography>
                    <Box onClick={() => handleView(row.doc_id, row)} sx={{ cursor: 'pointer' }}>
                      {columns.map(col => (
                        <Box key={col.id} sx={{ mb: 1.2, '&:last-child': { mb: 0 } }}>
                          <Typography variant="caption" sx={{ color: 'rgba(17,17,17,0.35)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 0.25 }}>
                            {col.label}
                          </Typography>
                          {col.render
                            ? col.render(row)
                            : <Typography variant="body2" sx={{ color: '#111111' }}>{row[col.id] || '—'}</Typography>}
                        </Box>
                      ))}
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}
        </Box>
        )}

        {filtered.length > perPage && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, pt: 2, borderTop: '1px solid rgba(17, 17, 17, 0.05)' }}>
            <ReactPaginate
              previousLabel="Prev" nextLabel="Next" breakLabel="..."
              pageCount={Math.ceil(filtered.length / perPage)}
              forcePage={page}
              marginPagesDisplayed={isMobile ? 0 : 1} pageRangeDisplayed={isMobile ? 2 : 3}
              onPageChange={({ selected: p }) => setPage(p)}
              containerClassName="pagination" activeClassName="active"
              previousClassName="page-item" nextClassName="page-item"
              pageClassName="page-item" breakClassName="page-item"
              pageLinkClassName="page-link" previousLinkClassName="page-link"
              nextLinkClassName="page-link" breakLinkClassName="page-link"
              activeLinkClassName="active-link"
            />
          </Box>
        )}
      </Box>

      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { background: 'transparent', border: '1px solid rgba(17, 17, 17, 0.1)', borderRadius: 1.5, boxShadow: '0 8px 32px rgba(0,0,0,0.4)', minWidth: 160 } }}>
        <MenuItem onClick={() => handleMenuAction('view')} sx={{ fontSize: '0.875rem', py: 1, color: '#111111' }}>
          <ListItemIcon><Visibility fontSize="small" sx={{ color: '#E8A020' }} /></ListItemIcon> View
        </MenuItem>
        {activeRow && getAssetDownloadUrl(activeRow) && (
          <MenuItem onClick={() => handleMenuAction('download')} sx={{ fontSize: '0.875rem', py: 1, color: '#111111' }}>
            <ListItemIcon><DownloadIcon fontSize="small" sx={{ color: '#C47F10' }} /></ListItemIcon> Download
          </MenuItem>
        )}
        <MenuItem onClick={() => handleMenuAction('share')} sx={{ fontSize: '0.875rem', py: 1, color: '#111111' }}>
          <ListItemIcon><Share fontSize="small" sx={{ color: '#C47F10' }} /></ListItemIcon> Share Link
        </MenuItem>
        <Divider sx={{ opacity: 0.1 }} />
        <MenuItem onClick={() => handleMenuAction('delete')} sx={{ fontSize: '0.875rem', py: 1, color: '#f43f5e' }}>
          <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon> Delete Forever
        </MenuItem>
      </Menu>

      <Snackbar open={snack.open} autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.sev} variant="filled" onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ borderRadius: '12px' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
